// Thin client for the unified Program endpoints (backend
// `app/routers/program.py`, prefix `/programs`; service `modules/program`,
// ADR-006 + ADR-006a). Programs are the unified replacement for the old
// coaching + programs modules: a Program is a shell (schedule + lifecycle) and
// Tracks are fee/capacity variants within it.
//
// IMPORTANT — types mirror backend `common/models.py` EXACTLY. The
// `programs.flow.json` mockup is ASPIRATIONAL and disagrees with the shipped
// backend in several places; the backend is the source of truth:
//   - program_type enum is "group | private | camp | clinic | league | package"
//     (flow mockup said group_class/clinic/camp/private_lesson — WRONG).
//   - create body is UnifiedProgramCreate (schedule_rrules / payee_type /
//     payee_id / start_date), NOT the flow's flat
//     {recurrence, price, instructor_select} shape.
//   - GET /programs returns a PAGINATED DICT {items, total}, not a bare array.
//   - status enum is "draft | published | cancelled | completed".
//   - a Track's fee/capacity fields are price / max_students / min_students
//     (flow said price / max_size — matches on price, not on max_size).
//   - Slice 6.1 (offering-keyed class pricing): a track's `price` is WRITE-ONLY
//     input on ProgramTrackCreate/Update — it seeds/updates a
//     `service="classes"`, `offering_id=track_id` pricing rule on the shared
//     facility pricing engine. ProgramTrackResponse does NOT carry a price
//     field at all (the backend dropped `fee_per_student` entirely, see Slice
//     6.1 Task 5) — read a track's current price via the facility's pricing
//     rules (`ratesApi.listPricingRules`), matching
//     `service === "classes" && offering_id === track_id`.
//   - cancel takes a {reason?} body; publish takes none.
//   - tracks are deleted via DELETE /programs/tracks/{track_id} (track-scoped
//     path, NOT program-scoped).
import { httpClient, programApi, userApi } from "@bw/shared/api";
import type { Money } from "@bw/shared/types";
import type {
  Schema,
  UnifiedProgramSessionResponse,
  UnifiedEnrollmentResponse,
  ProgramSessionOverrideCreate,
  ProgramSessionOverrideResponse,
  AttendanceBatchRequest,
  AttendanceBatchResponse,
  AttendanceRecordCreate,
  AttendanceRecordResponse,
  ProgramCoachCoverageResponse,
} from "@bw/shared/types/generated";

export type PublishProgramResponse = Schema["PublishProgramResponse"];
export type ProgramTrackOptionResponse = Schema["ProgramTrackOptionResponse"];
export type ProgramTrackOptionsPage = Schema["ProgramTrackOptionsPage"];
export type ProgramSessionPreviewPage = Schema["ProgramSessionPreviewPage"];
export type ProgramTrackOfferingLabelResponse =
  Schema["ProgramTrackOfferingLabelResponse"];
export type { ProgramCoachCoverageResponse };

export const listSessionPreview = programApi.getSessionPreview;
export const getTrack = programApi.getTrack;
export const getTrackOfferingLabel = programApi.getTrackOfferingLabel;
export type TeachingOutcome =
  | "completed"
  | "partial"
  | "coach_no_show"
  | "cancelled";
export interface ProgramTeachingContributor {
  coach_profile_id: string;
  role: "head" | "substitute" | "assistant";
  attributable_start: string;
  attributable_end: string;
}
export interface ProgramTeachingEvidenceWrite {
  track_id: string;
  occurrence_start: string;
  outcome: TeachingOutcome;
  actual_start?: string | null;
  actual_end?: string | null;
  contributors?: ProgramTeachingContributor[];
  correction_reason?: string | null;
  expected_revision?: number;
  idempotency_key: string;
}
export interface ProgramTeachingEvidenceCorrection {
  outcome: TeachingOutcome;
  actual_start?: string | null;
  actual_end?: string | null;
  contributors?: ProgramTeachingContributor[];
  correction_reason: string;
  expected_revision: number;
  idempotency_key: string;
}
export interface ProgramTeachingEvidenceRevoke {
  reason: string;
  expected_revision: number;
  idempotency_key: string;
}
export interface ProgramTeachingEvidenceResponse {
  evidence_id: string;
  revision: number;
  program_id: string;
  track_id: string;
  facility_id: string;
  occurrence_start: string;
  scheduled_start: string;
  scheduled_end: string;
  session_state: "scheduled" | "cancelled";
  actual_start?: string | null;
  actual_end?: string | null;
  outcome: TeachingOutcome;
  contributors: ProgramTeachingContributor[];
  source_kind: "club_attested";
  state: "active" | "revoked";
  correction_reason?: string | null;
  idempotency_key: string;
  attested_by: string;
  attested_at: string;
  qualifying_student_count: number;
}
export interface ProgramTeachingEvidenceHistoryPage {
  items: ProgramTeachingEvidenceResponse[];
  has_more: boolean;
  next_before_revision?: number | null;
}

// Re-export the generated types so component code imports them from the thin
// client (single seam), not directly from @bw/shared/types.
export type {
  UnifiedProgramSessionResponse,
  UnifiedEnrollmentResponse,
  ProgramSessionOverrideCreate,
  ProgramSessionOverrideResponse,
  AttendanceBatchRequest,
  AttendanceBatchResponse,
  AttendanceRecordCreate,
  AttendanceRecordResponse,
};

// AttendanceRecordCreate.status / AttendanceRecordResponse.status — the marking
// states. Mirrors the backend Literal["present","absent","late"]. Re-exported so
// the roll UI's 3-state control types against the single client seam.
export type AttendanceStatus = "present" | "absent" | "late";

// ---------------------------------------------------------------------------
// Enums (Literal unions mirroring backend Pydantic Literal types)
// ---------------------------------------------------------------------------

// UnifiedProgramCreate.program_type / ProgramListFilter.program_type
export type ProgramType =
  | "group"
  | "private"
  | "camp"
  | "clinic"
  | "league"
  | "package";

export const PROGRAM_TYPES: ProgramType[] = [
  "group",
  "private",
  "camp",
  "clinic",
  "league",
  "package",
];

// UnifiedProgramResponse.status / ProgramListFilter.status
export type ProgramStatus = "draft" | "published" | "cancelled" | "completed";

// UnifiedProgramCreate.payee_type
export type PayeeType = "facility" | "coach";

// ProgramTrackCreate.fee_type
export type TrackFeeType =
  | "per_session"
  | "per_series"
  | "per_month"
  | "per_package";

// ProgramTrackCreate.skill_level
export type TrackSkillLevel = "beginner" | "intermediate" | "advanced" | "all";

// ProgramTrackUpdate.status / ProgramTrackResponse.status
export type TrackStatus = "active" | "closed" | "cancelled";

// ---------------------------------------------------------------------------
// Track models — mirror common/models.py ProgramTrack* EXACTLY.
// Money fields (price) are number | string at runtime (Pydantic serializes
// Decimal as string for precision); never do raw arithmetic on them.
// ---------------------------------------------------------------------------

// ProgramTrackCreate. price >= 0 (Slice 6.1: write-side input that seeds the
// track's offering-keyed `classes` pricing rule — never persisted on the track
// itself); max_students >= 1; min_students >= 0 (default 1); age_max <= 120.
// The backend rejects max_students < min_students and age_max < age_min.
export interface ProgramTrackCreate {
  name: string;
  // Branded Money (ADR-005) so it can't be redeclared as a plain number/string
  // and can't be passed to raw arithmetic; form state holds a raw string and
  // converts via asMoney() at submit.
  price: Money;
  currency?: string; // default "USD"
  fee_type?: TrackFeeType; // default "per_series"
  enrollment_modes?: ("full_course" | "remaining_course" | "session_drop_in")[];
  late_join_cutoff_hours?: number;
  drop_in_price?: Money | null;
  min_students?: number; // default 1
  max_students: number;
  age_min?: number | null;
  age_max?: number | null;
  skill_level?: TrackSkillLevel; // default "all"
  schedule_rrules_override?: string[] | null;
  schedule_dtstart_override?: string | null; // ISO-8601 datetime
  // The courts every session of this track claims at publish time (Slice 3,
  // ADR-006a). Empty/omitted means the track claims no courts.
  default_court_ids?: string[];
}

// ProgramTrackUpdate — PATCH /programs/tracks/{track_id}; all optional.
// price: null/omitted = don't change the track's price; a concrete value
// re-syncs its offering-keyed pricing rule (see ProgramTrackCreate.price).
export interface ProgramTrackUpdate {
  name?: string;
  // Branded Money (ADR-005) — never a plain number/string; convert via asMoney().
  price?: Money | null;
  currency?: string;
  fee_type?: TrackFeeType;
  min_students?: number;
  max_students?: number;
  age_min?: number | null;
  age_max?: number | null;
  skill_level?: TrackSkillLevel;
  schedule_rrules_override?: string[] | null;
  schedule_dtstart_override?: string | null;
  status?: TrackStatus;
}

// ProgramTrackResponse. enrolled_count / waitlist_count / available_spots are
// server-computed. track_id / program_id are UUIDs — keys/payloads only, never
// rendered in the UI.
//
// Slice 6.1: NO price field here — the backend dropped `fee_per_student`
// entirely (a track's price IS its offering-keyed `classes` pricing rule, not
// a track column). Read a track's current price via the facility's pricing
// rules (see listFacilityTrackOfferings / ratesApi.listPricingRules), matching
// `service === "classes" && offering_id === track_id`.
export interface ProgramTrackResponse {
  track_id: string;
  program_id: string;
  name: string;
  currency: string;
  fee_type: string;
  min_students: number;
  max_students: number;
  age_min?: number | null;
  age_max?: number | null;
  skill_level: string;
  schedule_rrules_override?: string[] | null;
  schedule_dtstart_override?: string | null;
  enrolled_count: number;
  waitlist_count: number;
  available_spots: number;
  status: string;
  created_at: string;
  updated_at: string;
}

// ---------------------------------------------------------------------------
// Program models — mirror common/models.py UnifiedProgram* EXACTLY.
// ---------------------------------------------------------------------------

// UnifiedProgramCreate. facility_id is required; payee_id must be a valid UUID in
// that case (it is the facility_id). schedule_rrules are RFC-5545 RRULE
// strings (COUNT capped at 1000); timezone must be a valid IANA zone.
// schedule_duration_minutes: 0 < n <= 480. A program can be created as a shell
// (tracks: []) and have tracks added later.
export interface UnifiedProgramCreate {
  facility_id: string;
  coach_profile_id?: string | null;
  name: string;
  description?: string | null;
  program_type: ProgramType;
  schedule_rrules?: string[]; // default []
  schedule_dtstart?: string | null; // ISO-8601 datetime
  schedule_duration_minutes?: number; // default 60
  timezone?: string; // default "UTC"
  start_date: string; // ISO-8601 datetime
  end_date?: string | null; // ISO-8601 datetime; must be >= start_date
  payee_type?: PayeeType; // default "facility"
  payee_id: string; // facility UUID when payee_type == "facility"
  learning_space_enabled?: boolean;
  learning_space_post_permission?: "all_learners" | "staff_only";
  tracks?: ProgramTrackCreate[]; // default []
}

// UnifiedProgramUpdate — PATCH /programs/{program_id}. Backend forbids extra
// fields (model_config extra="forbid"). Immutable by design and NOT present:
// facility_id, payee_type, payee_id, status, created_by, coach_profile_id
// (instructor reassignment is the dedicated reassign-instructor endpoint).
export interface UnifiedProgramUpdate {
  name?: string;
  description?: string | null;
  program_type?: ProgramType;
  schedule_rrules?: string[];
  schedule_dtstart?: string | null;
  schedule_duration_minutes?: number;
  timezone?: string;
  start_date?: string;
  end_date?: string | null;
}

// UnifiedProgramResponse. status: draft | published | cancelled | completed.
// active_enrollment_count is server-computed. program_id / facility_id /
// payee_id are UUIDs — keys/payloads only, never rendered.
export interface UnifiedProgramResponse {
  program_id: string;
  audience: "catalog" | "private_coaching";
  facility_id?: string | null;
  coach_profile_id?: string | null;
  name: string;
  description?: string | null;
  program_type: string;
  schedule_rrules: string[];
  schedule_dtstart?: string | null;
  schedule_duration_minutes: number;
  timezone: string;
  start_date: string;
  end_date?: string | null;
  status: string;
  payee_type: string;
  payee_id: string;
  learning_space_enabled: boolean;
  learning_space_post_permission: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  active_enrollment_count: number;
}

// ---------------------------------------------------------------------------
// List filters + paginated envelope
// GET /programs returns {"items": UnifiedProgramResponse[], "total": number}.
// Filters map to ProgramListFilter (limit 1..500 default 50, offset >= 0).
// ---------------------------------------------------------------------------

export interface ProgramListParams {
  facility_id?: string;
  coach_profile_id?: string;
  status?: ProgramStatus;
  program_type?: ProgramType;
  limit?: number;
  offset?: number;
}

export interface ProgramListResult {
  items: UnifiedProgramResponse[];
  total: number;
}

interface ProgramListEnvelope {
  items: UnifiedProgramResponse[];
  total: number;
}

// ---------------------------------------------------------------------------
// Program CRUD + lifecycle
// ---------------------------------------------------------------------------

// GET /programs?facility_id=&coach_profile_id=&status=&program_type=&limit=&offset=
// Unwraps the {items, total} dict into a typed ProgramListResult.
export async function listPrograms(
  params: ProgramListParams = {},
  signal?: AbortSignal,
): Promise<ProgramListResult> {
  const qs = new URLSearchParams();
  if (params.facility_id) qs.set("facility_id", params.facility_id);
  if (params.coach_profile_id)
    qs.set("coach_profile_id", params.coach_profile_id);
  if (params.status) qs.set("status", params.status);
  if (params.program_type) qs.set("program_type", params.program_type);
  if (params.limit !== undefined) qs.set("limit", String(params.limit));
  if (params.offset !== undefined) qs.set("offset", String(params.offset));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  const envelope = await httpClient.get<ProgramListEnvelope>(
    `/programs${suffix}`,
    { signal },
  );
  return envelope;
}

export async function listProgramCoachCoverage(
  facilityId: string,
  signal?: AbortSignal,
): Promise<ProgramCoachCoverageResponse> {
  return httpClient.get<ProgramCoachCoverageResponse>(
    `/programs/coach-coverage?facility_id=${encodeURIComponent(facilityId)}`,
    { signal },
  );
}

// POST /programs — body UnifiedProgramCreate. Returns the created program in
// draft status.
export function createProgram(
  body: UnifiedProgramCreate,
): Promise<UnifiedProgramResponse> {
  return httpClient.post<UnifiedProgramResponse>("/programs", body);
}

export function listTeachingEvidence(
  programId: string,
  params: { trackId?: string; occurrenceStart?: string } = {},
): Promise<ProgramTeachingEvidenceResponse[]> {
  const query = new URLSearchParams({ limit: "10" });
  if (params.trackId) query.set("track_id", params.trackId);
  if (params.occurrenceStart)
    query.set("occurrence_start", params.occurrenceStart);
  return httpClient.get<ProgramTeachingEvidenceResponse[]>(
    `/programs/${encodeURIComponent(programId)}/teaching-evidence?${query.toString()}`,
  );
}

export function attestTeaching(
  programId: string,
  body: ProgramTeachingEvidenceWrite,
): Promise<ProgramTeachingEvidenceResponse> {
  return httpClient.put<ProgramTeachingEvidenceResponse>(
    `/programs/${encodeURIComponent(programId)}/teaching-evidence`,
    body,
  );
}

export function correctTeachingEvidence(
  programId: string,
  evidenceId: string,
  body: ProgramTeachingEvidenceCorrection,
): Promise<ProgramTeachingEvidenceResponse> {
  return httpClient.put<ProgramTeachingEvidenceResponse>(
    `/programs/${encodeURIComponent(programId)}/teaching-evidence/${encodeURIComponent(evidenceId)}`,
    body,
  );
}

export function listTeachingEvidenceHistory(
  programId: string,
  evidenceId: string,
  beforeRevision?: number,
): Promise<ProgramTeachingEvidenceHistoryPage> {
  const suffix = beforeRevision ? `?before_revision=${beforeRevision}` : "";
  return httpClient.get<ProgramTeachingEvidenceHistoryPage>(
    `/programs/${encodeURIComponent(programId)}/teaching-evidence/${encodeURIComponent(evidenceId)}/history${suffix}`,
  );
}

export function revokeTeachingEvidence(
  programId: string,
  evidenceId: string,
  body: ProgramTeachingEvidenceRevoke,
): Promise<ProgramTeachingEvidenceResponse> {
  return httpClient.post<ProgramTeachingEvidenceResponse>(
    `/programs/${encodeURIComponent(programId)}/teaching-evidence/${encodeURIComponent(evidenceId)}/revoke`,
    body,
  );
}

// GET /programs/{program_id}
export const getProgram = programApi.getProgram;

export function reassignProgramInstructor(
  programId: string,
  coachProfileId: string,
): Promise<UnifiedProgramResponse> {
  return httpClient.post<UnifiedProgramResponse>(
    `/programs/${encodeURIComponent(programId)}/reassign-instructor`,
    { new_coach_profile_id: coachProfileId },
  );
}

// POST /programs/{program_id}/publish — no body. draft -> published.
export function publishProgram(
  programId: string,
): Promise<PublishProgramResponse> {
  return httpClient.post<PublishProgramResponse>(
    `/programs/${programId}/publish`,
  );
}

// POST /programs/{program_id}/cancel — body {reason?}. published -> cancelled.
export function cancelProgram(
  programId: string,
  reason?: string,
): Promise<UnifiedProgramResponse> {
  return httpClient.post<UnifiedProgramResponse>(
    `/programs/${programId}/cancel`,
    { reason: reason ?? null },
  );
}

// ---------------------------------------------------------------------------
// Tracks
// ---------------------------------------------------------------------------

// GET /programs/{program_id}/tracks — one current owner option page.
export const listTracks = programApi.getTracks;

// POST /programs/{program_id}/tracks — body ProgramTrackCreate.
export function createTrack(
  programId: string,
  body: ProgramTrackCreate,
): Promise<ProgramTrackResponse> {
  return httpClient.post<ProgramTrackResponse>(
    `/programs/${programId}/tracks`,
    body,
  );
}

// DELETE /programs/tracks/{track_id} — track-scoped path (NOT program-scoped),
// returns 204.
export function deleteTrack(trackId: string): Promise<void> {
  return httpClient.delete<void>(`/programs/tracks/${trackId}`);
}

// ---------------------------------------------------------------------------
// Sessions (read-only this slice)
// ---------------------------------------------------------------------------

// GET /programs/{program_id}/sessions?window_start=&window_end=&track_id=
// Returns a BARE array of UnifiedProgramSessionResponse — RRULE-expanded
// occurrences within [window_start, window_end) with any program_session
// overrides applied on top. window_start / window_end are REQUIRED ISO-8601
// datetimes; track_id optionally narrows to a single track's schedule.
//
// Sessions are materialized server-side (not persisted); start_time/end_time
// are absolute instants and timezone is the IANA zone they should be rendered
// in — the UI must format with that zone, never the browser's.
export function listSessions(
  programId: string,
  windowStart: string,
  windowEnd: string,
  trackId?: string,
): Promise<UnifiedProgramSessionResponse[]> {
  const qs = new URLSearchParams();
  qs.set("window_start", windowStart);
  qs.set("window_end", windowEnd);
  if (trackId) qs.set("track_id", trackId);
  return httpClient.get<UnifiedProgramSessionResponse[]>(
    `/programs/${programId}/sessions?${qs.toString()}`,
  );
}

// ---------------------------------------------------------------------------
// Session overrides (cancel / reschedule a single RRULE occurrence)
//
// A session listSessions() returns is a MATERIALIZED occurrence (not persisted):
// it carries is_override + override_action + the server's exact nullable
// applied_override_id. The persisted override's occurrence_start is the BASE
// recurrence instant, not the moved/display time. So:
//   - To CREATE: POST occurrence_start = the session's base occurrence ISO,
//     byte-exact (never a program-local display string).
//   - To DELETE: use the displayed session's applied_override_id directly.
//     Never guess another Track/Program sibling from a matching date or list.
// Reschedule REQUIRES new_start + new_end (Pydantic validator → 422); the caller
// must guard that client-side. RBAC: facility:schedule, enforced server-side.
// ---------------------------------------------------------------------------

// POST /programs/{program_id}/session-overrides — body ProgramSessionOverrideCreate
// { track_id?, occurrence_start, action:"cancel"|"reschedule", new_start?, new_end?, reason? }.
export function createSessionOverride(
  programId: string,
  body: ProgramSessionOverrideCreate,
): Promise<ProgramSessionOverrideResponse> {
  return httpClient.post<ProgramSessionOverrideResponse>(
    `/programs/${programId}/session-overrides`,
    body,
  );
}

// GET /programs/{program_id}/session-overrides — bare array of the program's
// persisted overrides. This is an authorized fact list, not a client-side
// authority for choosing which override a materialized session is using.
export function listSessionOverrides(
  programId: string,
): Promise<ProgramSessionOverrideResponse[]> {
  return httpClient.get<ProgramSessionOverrideResponse[]>(
    `/programs/${programId}/session-overrides`,
  );
}

// DELETE /programs/session-overrides/{override_id} — override-scoped path (NOT
// program-scoped), returns 204. Removes the override so the occurrence reverts
// to its normal RRULE-expanded form.
export function deleteSessionOverride(overrideId: string): Promise<void> {
  return httpClient.delete<void>(`/programs/session-overrides/${overrideId}`);
}

// ---------------------------------------------------------------------------
// Enrollments (roster — read + cancel this slice; staff-add is unbuilt, #1346)
// UnifiedEnrollmentResponse is the generated type; we do NOT hand-mirror it.
// Fields used by the roster UI: enrollment_id, student_id (Firebase UID, NO
// display_name — resolve via getUserDisplayName), status, enrolled_at,
// fee_paid (Money — never raw arithmetic), currency, cancelled_at. Capacity is
// read off the already-loaded ProgramTrackResponse, NOT derived from this array.
// ---------------------------------------------------------------------------

// GET /programs/tracks/{track_id}/enrollments — bare array of
// UnifiedEnrollmentResponse for the track (track-scoped path).
export function listTrackEnrollments(
  trackId: string,
): Promise<UnifiedEnrollmentResponse[]> {
  return httpClient.get<UnifiedEnrollmentResponse[]>(
    `/programs/tracks/${trackId}/enrollments`,
  );
}

// POST /programs/enrollments/{enrollment_id}/cancel — body {reason?}. The
// backend handles refund + waitlist promotion server-side; mirrors cancelProgram.
export function cancelEnrollment(
  enrollmentId: string,
  reason?: string,
): Promise<UnifiedEnrollmentResponse> {
  return httpClient.post<UnifiedEnrollmentResponse>(
    `/programs/enrollments/${enrollmentId}/cancel`,
    { reason: reason ?? null },
  );
}

// Resolve a student_id (Firebase UID) -> a real display name via the shared user
// profile client (GET /users/{userId}/profile). The roster never renders a raw
// UUID; this wrapper is the single seam that turns an opaque id into a label.
//
// On ANY failure — network error, 403/404, or a blank/whitespace display_name —
// it returns null. Neither a full UID nor a fragment is a display name.
// Server-side roster name enrichment is
// tracked by #1347; until then the UI resolves per-UID through this wrapper.
export async function getUserDisplayName(
  userId: string,
): Promise<string | null> {
  try {
    const profile = await userApi.getProfile(userId);
    const name = profile.display_name?.trim();
    return name || null;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Attendance (the per-session roll — mark + read)
//
// A session's attendance is keyed by (track_id, occurrence_start), where
// occurrence_start is the session's BASE occurrence instant (UTC, byte-exact —
// the same value baseOccurrenceIso() resolves for an override). The roll's
// roster of who CAN be marked is the track's enrollments (listTrackEnrollments);
// these two clients only persist/read the marked statuses.
// ---------------------------------------------------------------------------

// PUT /programs/tracks/{track_id}/attendance — body AttendanceBatchRequest
// { track_id, occurrence_start, records:[{student_id, status}] }. Upserts (a
// re-PUT updates a student's status, no duplicate row). RBAC server-side
// (instructor OR facility:schedule). The path track_id must agree with the body
// — the backend 422s a mismatch — so the caller passes the SAME id to both.
export function markAttendance(
  trackId: string,
  body: AttendanceBatchRequest,
): Promise<AttendanceBatchResponse> {
  return httpClient.put<AttendanceBatchResponse>(
    `/programs/tracks/${trackId}/attendance`,
    body,
  );
}

// GET /programs/tracks/{track_id}/attendance?occurrence_start=<ISO> — bare array
// of AttendanceRecordResponse for that (track, base occurrence), ordered by
// student_id. Returns an EMPTY array when nothing is marked yet (NOT a 404), so
// the roll opens with an empty pre-fill rather than an error. occurrence_start is
// URL-encoded so a `+` in the ISO offset survives the query string verbatim.
export function listAttendance(
  trackId: string,
  occurrenceStart: string,
): Promise<AttendanceRecordResponse[]> {
  const qs = new URLSearchParams();
  qs.set("occurrence_start", occurrenceStart);
  return httpClient.get<AttendanceRecordResponse[]>(
    `/programs/tracks/${trackId}/attendance?${qs.toString()}`,
  );
}
